import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import {
  adminAuthCheck, adminListCities, adminUpdateCity,
  adminListHousing, adminUpdateHousing,
  adminListCol, adminUpdateCol,
  adminListBudgets, adminUpdateBudget,
  adminAuditLog,
} from "../lib/api";
import DataErrorNotice from "../components/DataErrorNotice";
import { Lock, LogOut, RefreshCw, AlertCircle, Save, ArrowLeft, History } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "hejpoland_admin_secret";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

// ---------- Inline edit row component ----------
function EditableRow({ row, idKey, editableFields, fieldLabels, onSave, testId }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const dirty = Object.keys(form).length > 0;

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => setForm({});

  const submit = async () => {
    setSaving(true);
    try {
      // Cast numeric fields
      const payload = {};
      for (const k of Object.keys(form)) {
        const t = editableFields[k];
        let v = form[k];
        if (t === "number") v = v === "" ? null : Number(v);
        if (t === "list") v = v.split(",").map(s => s.trim()).filter(Boolean);
        payload[k] = v;
      }
      await onSave(row[idKey], payload);
      toast.success("Modifié");
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <tr className="border-t border-slate-100 align-top" data-testid={testId}>
      {Object.entries(editableFields).map(([field, type]) => {
        const current = form[field] !== undefined ? form[field] : (Array.isArray(row[field]) ? row[field].join(", ") : row[field] ?? "");
        return (
          <td key={field} className="px-3 py-2">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-0.5">{fieldLabels[field] || field}</label>
            {type === "textarea" ? (
              <textarea
                value={current}
                onChange={(e) => change(field, e.target.value)}
                rows={2}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white focus:ring-1 focus:ring-[#0056D2] focus:border-transparent outline-none"
                data-testid={`field-${field}`}
              />
            ) : (
              <input
                type={type === "number" ? "number" : "text"}
                value={current}
                onChange={(e) => change(field, e.target.value)}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded bg-white focus:ring-1 focus:ring-[#0056D2] focus:border-transparent outline-none tabular-nums"
                data-testid={`field-${field}`}
              />
            )}
          </td>
        );
      })}
      <td className="px-3 py-2 whitespace-nowrap">
        <button onClick={submit} disabled={!dirty || saving} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition ${dirty ? "bg-[#0056D2] hover:bg-[#0047AD] text-white" : "bg-slate-100 text-slate-400 cursor-not-allowed"}`} data-testid="save-row">
          <Save className="w-3 h-3" /> {saving ? "..." : "Enregistrer"}
        </button>
        {dirty && <button onClick={reset} className="ml-1 text-xs text-slate-500 hover:text-slate-700">Annuler</button>}
        <div className="text-[10px] text-slate-400 mt-1">{fmtDate(row.last_updated)}</div>
      </td>
    </tr>
  );
}

// ---------- Tabs ----------
const TABS = [
  { id: "cities", label: "tabCities" },
  { id: "housing", label: "tabHousing" },
  { id: "col", label: "tabCol" },
  { id: "budgets", label: "tabBudgets" },
  { id: "audit", label: "tabAudit" },
];

export default function AdminData() {
  const { t } = useT();
  const [secret, setSecret] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [tab, setTab] = useState("cities");
  const [data, setData] = useState({ cities: [], housing: [], col: [], budgets: [], audit: [] });
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    if (!secret) return;
    adminAuthCheck(secret).then(() => setAuthed(true)).catch(() => {
      sessionStorage.removeItem(STORAGE_KEY); setSecret("");
    });
  }, []); // eslint-disable-line

  const loadTab = useCallback(async (which) => {
    setLoadErr(false);
    try {
      let payload = [];
      if (which === "cities") payload = await adminListCities(secret);
      else if (which === "housing") payload = await adminListHousing(secret);
      else if (which === "col") payload = await adminListCol(secret);
      else if (which === "budgets") payload = await adminListBudgets(secret);
      else if (which === "audit") payload = await adminAuditLog(secret, 30);
      setData((d) => ({ ...d, [which]: payload }));
    } catch (e) {
      console.error("[AdminData] load failed:", e?.message);
      setLoadErr(true);
    }
  }, [secret]);

  useEffect(() => { if (authed) loadTab(tab); }, [authed, tab, loadTab]);

  const login = async (e) => {
    e.preventDefault();
    setAuthError("");
    try {
      await adminAuthCheck(pwInput);
      sessionStorage.setItem(STORAGE_KEY, pwInput);
      setSecret(pwInput); setAuthed(true); setPwInput("");
    } catch { setAuthError(t("adminSources.invalidSecret")); }
  };

  const logout = () => { sessionStorage.removeItem(STORAGE_KEY); setSecret(""); setAuthed(false); };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24" data-testid="admin-data-login">
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <div className="w-12 h-12 rounded-2xl bg-[#0056D2]/10 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#0056D2]" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">{t("adminData.title")}</h1>
          <p className="text-sm text-slate-600 mt-2 mb-6">{t("adminSources.needSecret")}.</p>
          <form onSubmit={login} className="space-y-3">
            <input type="password" autoFocus value={pwInput} onChange={(e) => setPwInput(e.target.value)} placeholder={t("adminSources.promptSecret")} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0056D2] focus:border-transparent outline-none" data-testid="admin-data-secret-input" />
            {authError && <div className="flex items-center gap-2 text-sm text-[#DC143C]"><AlertCircle className="w-4 h-4" />{authError}</div>}
            <button type="submit" className="w-full bg-[#0056D2] hover:bg-[#0047AD] text-white font-medium py-2.5 rounded-full transition" data-testid="admin-data-login-submit">Se connecter</button>
          </form>
        </div>
      </div>
    );
  }

  // Field definitions per entity
  const cityFields = { name_fr: "text", population: "number", region: "text", summary: "textarea", image_url: "text" };
  const housingFields = { city_slug: "text", type: "text", zone: "text", rent_min: "number", rent_avg: "number", rent_max: "number", buy_per_m2: "number" };
  const colFields = { city_slug: "text", category: "text", item: "text", price_pln: "number", unit: "text" };
  const budgetFields = { city_slug: "text", rent: "number", food: "number", transport: "number", utilities: "number", leisure: "number", other: "number" };
  const FL = t("adminData.fields");

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 md:py-16" data-testid="admin-data-page">
      <header className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>Admin · CRUD</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-3">{t("adminData.title")}</h1>
          <p className="text-slate-600 mt-2 text-sm max-w-3xl">{t("adminData.intro")}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="../admin/sources" className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Sources</Link>
          <Link to="../admin/numbeo" className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5" data-testid="goto-admin-numbeo">Numbeo</Link>
          <button onClick={() => loadTab(tab)} className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50 text-sm flex items-center gap-1.5" data-testid="admin-data-reload"><RefreshCw className="w-3.5 h-3.5" /> Recharger</button>
          <button onClick={logout} className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-sm flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> {t("adminSources.logout")}</button>
        </div>
      </header>

      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === tb.id ? "border-[#0056D2] text-[#0056D2]" : "border-transparent text-slate-600 hover:text-slate-900"}`} data-testid={`tab-${tb.id}`}>
            {tb.id === "audit" && <History className="w-3.5 h-3.5 inline mr-1" />}
            {t(`adminData.${tb.label}`)}
          </button>
        ))}
      </div>

      {loadErr && <div className="mb-6"><DataErrorNotice onRetry={() => loadTab(tab)} /></div>}

      {tab === "cities" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm" data-testid="cities-table">
            <thead className="bg-slate-50 text-slate-600 text-xs"><tr><th className="px-3 py-2 text-left">Édition</th></tr></thead>
            <tbody>
              {data.cities.map((c) => (
                <EditableRow key={c.slug} row={c} idKey="slug" editableFields={cityFields} fieldLabels={FL} onSave={(id, u) => adminUpdateCity(id, u, secret)} testId={`row-city-${c.slug}`} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "housing" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm" data-testid="housing-table">
            <tbody>
              {data.housing.map((h) => (
                <EditableRow key={h.id} row={h} idKey="id" editableFields={housingFields} fieldLabels={FL} onSave={(id, u) => adminUpdateHousing(id, u, secret)} testId={`row-housing-${h.id}`} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "col" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm" data-testid="col-table">
            <tbody>
              {data.col.map((c) => (
                <EditableRow key={c.id} row={c} idKey="id" editableFields={colFields} fieldLabels={FL} onSave={(id, u) => adminUpdateCol(id, u, secret)} testId={`row-col-${c.id}`} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "budgets" && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm" data-testid="budgets-table">
            <tbody>
              {data.budgets.map((b) => (
                <EditableRow key={b.city_slug} row={b} idKey="city_slug" editableFields={budgetFields} fieldLabels={FL} onSave={(id, u) => adminUpdateBudget(id, u, secret)} testId={`row-budget-${b.city_slug}`} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "audit" && (
        <div>
          <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><History className="w-4 h-4 text-[#0056D2]" />{t("adminData.auditTitle")}</h2>
          {data.audit.length === 0 ? (
            <p className="text-sm text-slate-500 italic">{t("adminData.auditEmpty")}</p>
          ) : (
            <div className="space-y-3" data-testid="audit-log">
              {data.audit.map((entry) => (
                <div key={entry.id} className="bg-white border border-slate-200 rounded-xl p-4 text-sm" data-testid={`audit-${entry.id}`}>
                  <div className="flex justify-between items-start gap-3 flex-wrap">
                    <div>
                      <span className="font-semibold text-slate-900">{entry.entity}</span>
                      <span className="text-slate-500"> · {entry.entity_id}</span>
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums">{fmtDate(entry.ts)}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {entry.changes.map((ch, i) => (
                      <li key={i} className="text-xs text-slate-600 font-mono bg-slate-50 rounded px-2 py-1">
                        <span className="font-semibold text-slate-700">{ch.field}</span>
                        <span className="text-[#DC143C] line-through ml-2">{ch.old}</span>
                        <span className="text-slate-400 mx-1">→</span>
                        <span className="text-emerald-700">{ch.new}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
